import { Alert, Box, Button, CircularProgress, Collapse, Link, List, ListItem, ListItemText, TextField, Typography } from '@mui/material'
import { AutoAwesome } from '@mui/icons-material'
import { useEffect, useRef, useState } from 'react'
import type { ContractReaderConfig } from '../../../types/portalTypes/api/GetConfig'
import { Filled, Reading, readContract, readerNeedsPasscode } from './contractReader'
import { SectionHeader } from './OrderRequestFormComponents'

const CONFIDENCE_COLOR = { high: 'success.main', medium: 'warning.main', low: 'error.main' } as const
const CONFIDENCE_TEXT = { high: 'Sure', medium: 'Fairly sure', low: 'Not sure: check it' } as const
const NOT_RATED = 'Not rated: check it'

function Dot({ color, title }: { color: string; title: string }) {
  return <Box component="span" title={title} sx={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', mr: 1, flexShrink: 0, bgcolor: color }} />
}

/**
 * Read with AI: the agent drops in the purchase agreement and the reader fills in the form.
 * Everything it fills stays editable; the list below says where each value came from.
 */
export function ContractReaderPanel({ reader, onReading, filled }: {
  reader: ContractReaderConfig; onReading: (reading: Reading) => void; filled: Filled[]
}) {
  const [files, setFiles] = useState<File[]>([])
  const [needsPasscode, setNeedsPasscode] = useState(false)
  const [passcode, setPasscode] = useState('')
  const [progress, setProgress] = useState('')
  const [error, setError] = useState('')
  const [reading, setReading] = useState<Reading | null>(null)
  const [showSources, setShowSources] = useState(false)
  const abort = useRef<AbortController | null>(null)
  const picker = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    readerNeedsPasscode(reader.url).then(setNeedsPasscode).catch(() => setNeedsPasscode(true))
    return () => abort.current?.abort()
  }, [reader.url])

  const busy = !!progress
  const pdfs = files.filter((f) => f.type === 'application/pdf')
  const fileProblem = pdfs.length && files.length > 1 ? 'Choose one PDF, or a photo of each page.' : ''

  async function read() {
    abort.current?.abort()
    abort.current = new AbortController()
    setError('')
    setReading(null)
    try {
      await readContract(files, {
        baseUrl: reader.url,
        uploadHost: reader.uploadHost,
        passcode,
        signal: abort.current.signal,
        onProgress: setProgress,
        onReading: (r) => { setReading(r); onReading(r) },
      })
    } catch (e: any) {
      if (!abort.current?.signal.aborted) setError(e?.message || 'Reading failed.')
    } finally {
      setProgress('')
    }
  }

  const stats = reading?.stats

  return (
    <Box sx={{ p: 2, mb: 1, border: '1px solid', borderColor: 'primary.light', borderRadius: 1, bgcolor: 'action.hover' }}>
      <SectionHeader title="Read with AI (demo)" />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Add the purchase agreement and AI fills in the form below. Check every value before you submit.
        Use only sample contracts while this is a demo: the file goes to the demo reader, not to this order.
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        {/* A real button (Enter and Space work) that opens the hidden file picker. */}
        <Button variant="outlined" disabled={busy} onClick={() => picker.current?.click()}>
          {files.length ? `${files.length === 1 ? files[0].name : `${files.length} photos`}` : 'Choose agreement'}
        </Button>
        <input ref={picker} type="file" hidden multiple accept=".pdf,.jpg,.jpeg,.png" aria-label="Purchase agreement"
          onChange={(e) => { setFiles(Array.from(e.target.files ?? [])); e.target.value = '' }} />
        {needsPasscode && (
          <TextField size="small" type="password" label="Passcode" value={passcode} autoComplete="off" disabled={busy}
            onChange={(e) => setPasscode(e.target.value)} />
        )}
        <Button variant="contained" startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome />}
          disabled={busy || !files.length || !!fileProblem || (needsPasscode && !passcode)} onClick={read}>
          {busy ? progress : 'Read with AI'}
        </Button>
      </Box>
      {fileProblem && <Typography color="error" variant="body2" sx={{ mt: 1 }}>{fileProblem}</Typography>}
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

      {reading && (
        <Box sx={{ mt: 2 }}>
          <Alert severity="success">
            Filled in {filled.length} values{stats?.seconds ? ` in ${Math.round(stats.seconds)} seconds` : ''}. Check them before you submit.{' '}
            <Link component="button" type="button" onClick={() => setShowSources((s) => !s)}>{showSources ? 'Hide' : 'Show'} where each came from</Link>
          </Alert>
          {reading.concerns?.length > 0 && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              The document has text aimed at an AI. It was not followed:
              <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                {reading.concerns.map((c, i) => <li key={i}>{c.message}{c.page ? ` (page ${c.page})` : ''}</li>)}
              </ul>
            </Alert>
          )}
          <Collapse in={showSources}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1, typography: 'caption', color: 'text.secondary' }}>
              How sure the AI was:
              {(['high', 'medium', 'low'] as const).map((c) => (
                <Box key={c} component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}><Dot color={CONFIDENCE_COLOR[c]} title={CONFIDENCE_TEXT[c]} />{CONFIDENCE_TEXT[c]}</Box>
              ))}
              {filled.some((f) => !f.auto && !f.confidence) && (
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}><Dot color="grey.400" title={NOT_RATED} />{NOT_RATED}</Box>
              )}
            </Box>
            <List dense>
              {filled.map((f, i) => (
                <ListItem key={i} disableGutters>
                  {f.auto
                    ? <Box component="span" sx={{ width: 10, mr: 1, flexShrink: 0 }} />
                    : <Dot color={f.confidence ? CONFIDENCE_COLOR[f.confidence] : 'grey.400'} title={f.confidence ? CONFIDENCE_TEXT[f.confidence] : NOT_RATED} />}
                  <ListItemText primary={`${f.label}: ${f.value}`}
                    secondary={f.auto ? f.why ?? 'Set automatically: this is a purchase agreement'
                      : [`AI: ${f.confidence ? CONFIDENCE_TEXT[f.confidence] : NOT_RATED}`, f.page ? `Page ${f.page}` : '',
                        f.quote && f.quote !== f.value ? `“${f.quote}”` : ''].filter(Boolean).join(' · ')} />
                </ListItem>
              ))}
            </List>
          </Collapse>
        </Box>
      )}

    </Box>
  )
}
